import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "./button";
import { cn } from "./cn";
import { EmptyState, ErrorState, Skeleton } from "./feedback";
import { formatNumber } from "./format";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  /** Kept for API compatibility; narrow layouts now switch to stacked cards instead of hiding columns. */
  hideOnMobile?: boolean;
}

/** Containers narrower than this always use the stacked card layout. */
const CARD_BELOW = 480;

/**
 * Table that adapts to its container: when the columns don't fit (phones, narrow
 * panels, split layouts) rows render as stacked cards instead of a clipped,
 * sideways-scrolling table. Cards keep table/row/cell roles so assistive tech
 * and tests see the same structure.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  onRetry,
  empty,
  onRowClick,
  skeletonRows = 6,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  skeletonRows?: number;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [needed, setNeeded] = useState(0);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([e]) => e && setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cards = width != null && (width < CARD_BELOW || width + 1 < needed);

  // While rendered as a table, record the width it needs only when it overflows
  // (cells may wrap first); cards are used until the container is that wide again.
  useLayoutEffect(() => {
    const t = table.current;
    const box = t?.parentElement;
    if (!t || !box || cards) return;
    setNeeded(t.scrollWidth > box.clientWidth + 1 ? t.scrollWidth : 0);
  }, [cards, rows, columns, loading, width]);

  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  const isAction = (c: Column<T>) => c.header === "" || c.header == null;
  const [first, ...rest] = columns;
  const actions = rest.filter(isAction);
  const fields = rest.filter((c) => !isAction(c));

  return (
    <div ref={wrap} className="min-w-0">
      {cards ? (
        <div role="table" className="flex flex-col divide-y divide-line">
          {loading
            ? Array.from({ length: Math.min(skeletonRows, 4) }, (_, i) => (
                <div key={i} className="flex flex-col gap-2 px-4 py-3.5">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              ))
            : rows?.map((row) => (
                <div
                  key={rowKey(row)}
                  role="row"
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn("flex flex-col gap-2.5 px-4 py-3.5 text-[13px]", onRowClick && "cursor-pointer active:bg-surface-2 hover:bg-accent-soft/30")}
                >
                  {first && (
                    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                      <div role="cell" className="min-w-0 flex-[1_1_11rem] font-medium break-words">{first.cell(row)}</div>
                      {actions.map((c) => (
                        <div key={c.key} role="cell" className="max-w-full shrink-0" onClick={(e) => e.stopPropagation()}>{c.cell(row)}</div>
                      ))}
                    </div>
                  )}
                  {fields.length > 0 && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 min-[560px]:grid-cols-3">
                      {fields.map((c) => (
                        <div key={c.key} className="min-w-0">
                          <dt className="text-[10.5px] font-medium tracking-wide text-subtle uppercase" aria-hidden>{c.header}</dt>
                          <dd role="cell" className="mt-0.5 min-w-0 break-words text-muted">{c.cell(row)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table ref={table} className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line bg-surface-2/70">
                {columns.map((c) => (
                  <th key={c.key} scope="col" className={cn("px-5 py-2.5 text-left text-[11px] font-semibold tracking-wider whitespace-nowrap text-subtle uppercase", c.className)}>
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: skeletonRows }, (_, i) => (
                    <tr key={i} className="border-b border-line last:border-0">
                      {columns.map((c) => (
                        <td key={c.key} className="px-5 py-3">
                          <Skeleton className="h-3.5 w-[70%]" />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows?.map((row) => (
                    <tr
                      key={rowKey(row)}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn("border-b border-line transition-colors last:border-0 hover:bg-surface-2/50", onRowClick && "cursor-pointer hover:bg-accent-soft/40")}
                    >
                      {columns.map((c) => (
                        <td key={c.key} className={cn("px-5 py-3 align-middle", c.className)}>
                          {c.cell(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && rows && rows.length === 0 && (empty ?? <EmptyState title="Nothing here yet" />)}
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage(p: number): void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-[12.5px] text-muted">
      <span className="tabular-nums">
        {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}
      </span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page" icon={<ChevronLeft size={14} />} />
        <span className="px-1 tabular-nums">
          {page} / {pages}
        </span>
        <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page" icon={<ChevronRight size={14} />} />
      </div>
    </div>
  );
}
